import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Grid,
  Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip, CircularProgress, LinearProgress,
  TextField, Select, MenuItem, FormControl, InputLabel, Switch,
  FormControlLabel, Tabs, Tab,
} from '@mui/material';
import {
  Backup, Assessment, HealthAndSafety, Download, Delete, Restore,
  Refresh, CheckCircle, Warning, Error as ErrorIcon, CloudDownload,
  Add, People as PeopleIcon, Edit as EditIcon, Storage, Dns, Speed,
} from '@mui/icons-material';
import ModuleHeader from '../components/ModuleHeader';
import api from '../axios';

const TEAL = '#00F1A7';
const LIME = '#B4F300';

const STATUS_CORES = {
  ok:      { color: TEAL,     bg: 'rgba(0,241,167,0.16)' },
  healthy: { color: '#18D7A0', bg: 'rgba(24,215,160,0.16)' },
  warning: { color: '#FFC24B', bg: 'rgba(255,194,75,0.16)' },
  error:   { color: '#FF5C5C', bg: 'rgba(255,92,92,0.16)' },
};

function statusColor(status) {
  if (status === 'ok') return STATUS_CORES.ok;
  if (status === 'healthy') return STATUS_CORES.healthy;
  if (status && status.includes('warning')) return STATUS_CORES.warning;
  return STATUS_CORES.error;
}

function percentColor(percent) {
  return percent > 80 ? '#FF5C5C' : TEAL;
}

function AdminDashboard() {
  const [loading, setLoading] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backups, setBackups] = useState([]);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [openBackupDialog, setOpenBackupDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [openImportConfirmDialog, setOpenImportConfirmDialog] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [activeTab, setActiveTab] = useState('health');
  const [usuarios, setUsuarios] = useState([]);
  const [dialogUser, setDialogUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userForm, setUserForm] = useState({ nome: '', email: '', senha: '', tipo: 'aluno', ativo: true });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        setIsSuperAdmin(payload.tipo === 'super_admin');
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'health') {
      fetchHealth();
    } else if (activeTab === 'backups') {
      fetchBackups();
    } else if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab]);

  const fetchHealth = async () => {
    setLoading(true);
    setError('');
    console.log('🏥 Buscando informações de saúde do sistema...');
    try {
      const res = await api.get('/admin/health');
      console.log('✅ Health data recebida:', res.data);
      
      // Se o backend retornou com status "error", mostrar o erro
      if (res.data.status === 'error') {
        setError(`Erro no servidor: ${res.data.error}`);
        setHealth(null);
      } else {
        setHealth(res.data);
        setError('');
      }
    } catch (err) {
      console.error('❌ Erro ao buscar health:', err);
      console.error('Response:', err.response?.data);
      setError(errorMsg(err) || 'Erro ao buscar informações de saúde');
      setHealth(null);
    }
    setLoading(false);
  };

  const fetchBackups = async () => {
    setLoading(true);
    setError('');
    console.log('💾 Buscando lista de backups...');
    try {
      const res = await api.get('/admin/backups');
      console.log('✅ Backups recebidos:', res.data);
      setBackups(res.data.backups || []);
      setError('');
    } catch (err) {
      console.error('❌ Erro ao buscar backups:', err);
      setError(errorMsg(err) || 'Erro ao buscar backups');
    }
    setLoading(false);
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/logs');
      setLogs(res.data.logs || []);
      setError('');
    } catch (err) {
      setError('Erro ao buscar logs');
      console.error(err);
    }
    setLoading(false);
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const res = await api.get('/usuarios/');
      setUsuarios(res.data || []);
      setError('');
    } catch (err) {
      setError('Erro ao buscar usuarios');
    }
    setLoading(false);
  };

  const openNewUser = () => {
    setEditUser(null);
    setUserForm({ nome: '', email: '', senha: '', tipo: 'aluno', ativo: true });
    setDialogUser(true);
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setUserForm({ nome: u.nome, email: u.email, senha: '', tipo: u.tipo, ativo: u.ativo });
    setDialogUser(true);
  };

  const saveUser = async () => {
    try {
      if (editUser) {
        await api.put(`/usuarios/${editUser.id}`, userForm);
      } else {
        await api.post('/usuarios/', userForm);
      }
      setDialogUser(false);
      setSuccess('Usuario salvo com sucesso');
      fetchUsuarios();
    } catch (err) {
      setError(errorMsg(err) || 'Erro ao salvar usuario');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Excluir este usuario?')) return;
    try {
      await api.delete(`/usuarios/${id}`);
      fetchUsuarios();
      setSuccess('Usuario excluido');
    } catch (err) {
      setError(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const toggleUser = async (id) => {
    try {
      await api.patch(`/usuarios/${id}/ativar`);
      fetchUsuarios();
    } catch (err) {
      setError(errorMsg(err) || 'Erro');
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/admin/backup');
      setSuccess(`Backup criado: ${res.data.filename} (${res.data.size_mb} MB)`);
      setOpenBackupDialog(false);
      setTimeout(() => fetchBackups(), 1000);
    } catch (err) {
      setError('Erro ao criar backup: ' + (errorMsg(err) || err.message));
    }
    setLoading(false);
  };

  const handleDownloadBackup = async (filename) => {
    try {
      setDownloadingBackup(true);
      setDownloadProgress(0);
      setError('');
      setSuccess('');
      
      console.log('🔽 Iniciando download do backup:', filename);
      console.log('🔑 Token disponível:', !!localStorage.getItem('token'));
      
      const response = await api.get(`/admin/backups/${filename}`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('📊 Progresso:', percentCompleted + '%');
          setDownloadProgress(percentCompleted);
        }
      });
      
      console.log('✅ Download concluído, criando arquivo...');
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('💾 Arquivo salvo com sucesso!');
      setSuccess('Backup baixado com sucesso!');
      setDownloadProgress(100);
    } catch (err) {
      console.error('❌ Erro no download:', err);
      console.error('Response:', err.response);
      setError(errorMsg(err) || err.message || 'Erro ao baixar backup');
    } finally {
      setTimeout(() => {
        setDownloadingBackup(false);
        setDownloadProgress(0);
      }, 2000);
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Tem certeza que deseja deletar o backup ${filename}?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await api.delete(`/admin/backups/${filename}`);
      setSuccess('Backup deletado com sucesso');
      fetchBackups();
    } catch (err) {
      setError('Erro ao deletar backup: ' + (errorMsg(err) || err.message));
    }
    setLoading(false);
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/admin/restore/${selectedBackup}`);
      setSuccess(`Backup restaurado! ${res.data.message}`);
      setOpenRestoreDialog(false);
      setSelectedBackup(null);
      // Recarregar página após 2 segundos
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      setError('Erro ao restaurar backup: ' + (errorMsg(err) || err.message));
    }
    setLoading(false);
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    setUploadingBackup(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', 'true');

      const res = await api.post('/admin/import-backup', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportPreview(res.data);
      setPendingImportFile(file);
      setOpenImportConfirmDialog(true);
    } catch (err) {
      setError('Erro ao importar backup: ' + (errorMsg(err) || err.message));
    } finally {
      setUploadingBackup(false);
    }
  };

  const handleConfirmImportBackup = async () => {
    if (!pendingImportFile) return;

    setUploadingBackup(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', pendingImportFile);
      formData.append('dry_run', 'false');

      const res = await api.post('/admin/import-backup', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const resumo = res.data?.resumo;
      if (resumo) {
        setSuccess(
          `Backup importado: ${res.data.filename} (${res.data.size_mb} MB) | ` +
          `Tabelas: ${resumo.tabelas_processadas}, ` +
          `Inseridas: ${resumo.linhas_inseridas || 0}, ` +
          `Atualizadas: ${resumo.linhas_atualizadas || 0}`
        );
      } else {
        setSuccess(`Backup importado: ${res.data.filename} (${res.data.size_mb} MB)`);
      }

      setOpenImportConfirmDialog(false);
      setImportPreview(null);
      setPendingImportFile(null);
      fetchBackups();
    } catch (err) {
      setError('Erro ao confirmar importação: ' + (errorMsg(err) || err.message));
    } finally {
      setUploadingBackup(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'ok' || status === 'healthy') {
      return <CheckCircle color="success" />;
    } else if (status && status.includes('warning')) {
      return <Warning color="warning" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const tabKeys = isSuperAdmin ? ['usuarios', 'health', 'logs', 'backups'] : ['usuarios', 'health', 'logs'];
  const tabLabels = { usuarios: 'Usuários', health: 'Saúde do Sistema', logs: 'Logs', backups: 'Backups' };
  const tabIcons = { usuarios: <PeopleIcon />, health: <HealthAndSafety />, logs: <Assessment />, backups: <Backup /> };

  const handleTabChange = (_, index) => {
    const key = tabKeys[index];
    setActiveTab(key);
    if (key === 'usuarios') fetchUsuarios();
    if (key === 'health') fetchHealth();
    if (key === 'logs') fetchLogs();
    if (key === 'backups') fetchBackups();
  };

  const registros = [
    { k: 'Usuários', v: health?.database?.registros?.usuarios },
    { k: 'Alunos', v: health?.database?.registros?.alunos },
    { k: 'Professores', v: health?.database?.registros?.professores },
    { k: 'Turmas', v: health?.database?.registros?.turmas },
    { k: 'Aulas', v: health?.database?.registros?.aulas },
    { k: 'Frequências', v: health?.database?.registros?.frequencias },
    { k: 'Consolidadores', v: health?.database?.registros?.consolidadores },
    { k: 'Visitantes', v: health?.database?.registros?.visitantes },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Sistema" title="Administração" />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs
        value={tabKeys.indexOf(activeTab)}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, textTransform: 'none' },
        }}
        TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabKeys.map(k => <Tab key={k} label={tabLabels[k]} icon={tabIcons[k]} iconPosition="start" />)}
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Saúde do Sistema */}
      {activeTab === 'health' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              <HealthAndSafety sx={{ mr: 1, verticalAlign: 'middle', color: TEAL }} />
              Saúde do Sistema
            </Typography>
            <Button startIcon={<Refresh />} onClick={fetchHealth}>Atualizar</Button>
          </Box>

          {!health ? (
            <Alert severity="warning">Nenhum dado. Clique em Atualizar.</Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={3}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: statusColor(health.database?.status).color, display: 'flex' }}>
                          {getStatusIcon(health.database?.status)}
                        </Box>
                        <Typography variant="caption" color="text.secondary">Banco de Dados</Typography>
                      </Box>
                      <Chip
                        label={health.database?.status || 'N/A'}
                        size="small"
                        sx={{ bgcolor: statusColor(health.database?.status).bg, color: statusColor(health.database?.status).color, fontWeight: 700 }}
                      />
                      <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: '2rem', color: statusColor(health.database?.status).color, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {health.database?.size_mb?.toFixed(1) ?? 0} MB
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: percentColor(health.sistema?.memoria_percent), display: 'flex' }}><Speed fontSize="small" /></Box>
                        <Typography variant="caption" color="text.secondary">Memória</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: percentColor(health.sistema?.memoria_percent), lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {health.sistema?.memoria_percent?.toFixed(0) ?? 0}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {health.sistema?.memoria_disponivel_mb?.toFixed(0) ?? 0} MB disponíveis
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: percentColor(health.sistema?.disco_percent), display: 'flex' }}><Dns fontSize="small" /></Box>
                        <Typography variant="caption" color="text.secondary">Disco</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: percentColor(health.sistema?.disco_percent), lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {health.sistema?.disco_percent?.toFixed(0) ?? 0}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {health.sistema?.disco_disponivel_gb?.toFixed(1) ?? 0} GB disponíveis
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={3}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: TEAL, display: 'flex' }}><CloudDownload fontSize="small" /></Box>
                        <Typography variant="caption" color="text.secondary">Uploads</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: TEAL, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {health.uploads?.size_mb?.toFixed(1) ?? 0} MB
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {health.uploads?.status === 'ok' ? 'Normal' : (health.uploads?.status || 'N/A')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: LIME, display: 'flex' }}><Backup fontSize="small" /></Box>
                        <Typography variant="caption" color="text.secondary">Backups</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '4.4rem', color: LIME, lineHeight: 1 }}>
                        {health.backups?.total ?? 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Último: {health.backups?.ultimo ? new Date(health.backups.ultimo).toLocaleString('pt-BR') : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ borderRadius: 1 }}>
                <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Storage sx={{ color: TEAL }} />
                  <Typography sx={{ fontWeight: 700 }}>Registros no banco de dados</Typography>
                </Box>
                <Grid container spacing={2} sx={{ p: 2 }}>
                  {registros.map(r => (
                    <Grid item xs={6} sm={4} md={3} key={r.k}>
                      <Card sx={{ borderRadius: 1 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="caption" color="text.secondary">{r.k}</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '4.4rem', color: TEAL, lineHeight: 1 }}>
                            {r.v ?? 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </>
          )}
        </>
      )}

      {/* Usuários */}
      {activeTab === 'usuarios' && (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 700 }}>
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', color: TEAL }} />
                Gestão de Usuários
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Refresh />} onClick={fetchUsuarios}>Atualizar</Button>
                <Button variant="contained" startIcon={<Add />} onClick={openNewUser}>Novo Usuário</Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Nome</strong></TableCell>
                    <TableCell><strong>E-mail</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">Nenhum usuário</TableCell></TableRow>
                  ) : (
                    usuarios.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>{u.nome}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={u.tipo} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={u.ativo ? 'Ativo' : 'Inativo'}
                              size="small"
                              sx={u.ativo
                                ? { bgcolor: 'rgba(24,215,160,0.16)', color: '#18D7A0', fontWeight: 700 }
                                : { bgcolor: 'rgba(148,163,184,0.16)', color: '#94A3B8', fontWeight: 700 }}
                            />
                            <Tooltip title={u.ativo ? 'Desativar' : 'Ativar'}>
                              <Switch checked={u.ativo} onChange={() => toggleUser(u.id)} size="small" />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEditUser(u)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => deleteUser(u.id)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Backups */}
      {activeTab === 'backups' && (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 700 }}>
                <Backup sx={{ mr: 1, verticalAlign: 'middle', color: TEAL }} />
                Gestão de Backups
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  startIcon={<Refresh />}
                  onClick={fetchBackups}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  Atualizar
                </Button>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploadingBackup ? <CircularProgress size={16} /> : <Download />}
                  disabled={loading || uploadingBackup}
                  sx={{ mr: 1 }}
                >
                  {uploadingBackup ? 'Importando...' : 'Importar Backup'}
                  <input
                    hidden
                    type="file"
                    accept=".tar.gz,.tgz"
                    onChange={handleImportBackup}
                  />
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Backup />}
                  onClick={() => setOpenBackupDialog(true)}
                  disabled={loading}
                >
                  Criar Backup
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Backups automáticos:</strong> O sistema cria backups automaticamente todos os dias entre 0h e 3h.
            </Alert>

            <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Arquivo</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Tamanho</strong></TableCell>
                    <TableCell><strong>Data de Criação</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Nenhum backup disponível
                      </TableCell>
                    </TableRow>
                  ) : (
                    backups.map((backup) => (
                      <TableRow key={backup.filename} hover>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{backup.filename}</TableCell>
                        <TableCell>
                          <Chip
                            label={backup.tipo}
                            color={backup.tipo === 'manual' ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{backup.size_mb} MB</TableCell>
                        <TableCell>
                          {new Date(backup.created_at).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Baixar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadBackup(backup.filename)}
                            >
                              <CloudDownload />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restaurar">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setSelectedBackup(backup.filename);
                                setOpenRestoreDialog(true);
                              }}
                            >
                              <Restore />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBackup(backup.filename)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Logs */}
      {activeTab === 'logs' && (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 700 }}>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle', color: TEAL }} />
                Logs de Administração
              </Typography>
              <Button
                startIcon={<Refresh />}
                onClick={fetchLogs}
                disabled={loading}
              >
                Atualizar
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Data/Hora</strong></TableCell>
                    <TableCell><strong>Usuário</strong></TableCell>
                    <TableCell><strong>Ação</strong></TableCell>
                    <TableCell><strong>Detalhes</strong></TableCell>
                    <TableCell><strong>IP</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Nenhum log encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          {new Date(log.data_hora).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>{log.usuario_email}</TableCell>
                        <TableCell>
                          <Chip label={log.acao} size="small" color="primary" />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.detalhes || '-'}
                        </TableCell>
                        <TableCell>{log.ip_address || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Criar Backup */}
      <Dialog open={openBackupDialog} onClose={() => !loading && setOpenBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Criar Backup Manual</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Um novo backup será criado com todos os dados do sistema, incluindo:
          </Typography>
          <ul>
            <li>Banco de dados completo</li>
            <li>Arquivos de upload (documentos, PDFs, etc)</li>
          </ul>
          <Alert severity="warning" sx={{ mt: 2 }}>
            O backup pode levar alguns segundos para ser criado.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBackupDialog(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateBackup}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Backup />}
          >
            {loading ? 'Criando...' : 'Criar Backup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Restaurar Backup */}
      <Dialog open={openRestoreDialog} onClose={() => !loading && setOpenRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ Restaurar Backup
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>ATENÇÃO!</strong> Esta ação irá substituir TODOS os dados atuais do sistema!
          </Alert>
          <Typography variant="body2" gutterBottom>
            Você está prestes a restaurar o backup:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
            {selectedBackup}
          </Typography>
          <Typography variant="body2">
            Um backup de segurança será criado antes da restauração, mas <strong>não é recomendado fazer isso em produção</strong> sem certeza absoluta.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestoreDialog(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleRestoreBackup}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Restore />}
          >
            {loading ? 'Restaurando...' : 'Confirmar Restauração'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Download com Progresso */}
      <Dialog open={downloadingBackup} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudDownload color="primary" />
            Baixando Backup
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {downloadProgress < 100 
                ? `Baixando arquivo... ${downloadProgress}%` 
                : 'Download concluído! ✅'}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={downloadProgress} 
              sx={{ height: 8, borderRadius: 4, mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Por favor, aguarde enquanto o arquivo é baixado.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar Importação após Simulação */}
      <Dialog
        open={openImportConfirmDialog}
        onClose={() => !uploadingBackup && setOpenImportConfirmDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          ⚠️ Confirmar Importação Incremental
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Simulação concluída. Nenhum dado foi alterado até aqui.
          </Alert>

          <Typography variant="body2" gutterBottom>
            Revise o impacto previsto antes de confirmar:
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2"><strong>Tabelas processadas:</strong> {importPreview?.resumo?.tabelas_processadas || 0}</Typography>
            <Typography variant="body2"><strong>Linhas no backup:</strong> {importPreview?.resumo?.linhas_no_backup || 0}</Typography>
            <Typography variant="body2"><strong>Linhas a inserir:</strong> {importPreview?.resumo?.linhas_inseridas || 0}</Typography>
            <Typography variant="body2"><strong>Linhas a atualizar:</strong> {importPreview?.resumo?.linhas_atualizadas || 0}</Typography>
            <Typography variant="body2"><strong>Uploads novos:</strong> {importPreview?.resumo?.uploads_novos || 0}</Typography>
            <Typography variant="body2"><strong>Uploads diferentes:</strong> {importPreview?.resumo?.uploads_diferentes || 0}</Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            A confirmação aplica merge incremental: insere registros ausentes e atualiza registros divergentes, sem apagar a base atual.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenImportConfirmDialog(false);
              setImportPreview(null);
              setPendingImportFile(null);
            }}
            disabled={uploadingBackup}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmImportBackup}
            variant="contained"
            color="warning"
            disabled={uploadingBackup}
            startIcon={uploadingBackup ? <CircularProgress size={16} /> : <Download />}
          >
            {uploadingBackup ? 'Importando...' : 'Confirmar Importação'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Criar/Editar Usuario */}
      <Dialog open={dialogUser} onClose={() => setDialogUser(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Editar Usuario' : 'Novo Usuario'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nome" value={userForm.nome} onChange={e => setUserForm(f => ({ ...f, nome: e.target.value }))} sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label={editUser ? 'Nova senha (deixe em branco para manter)' : 'Senha'} type="password" value={userForm.senha} onChange={e => setUserForm(f => ({ ...f, senha: e.target.value }))} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={userForm.tipo} label="Tipo" onChange={e => setUserForm(f => ({ ...f, tipo: e.target.value }))}>
              {['admin', 'professor', 'aluno', 'consolidador', 'secretaria', 'pastor'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch checked={userForm.ativo} onChange={e => setUserForm(f => ({ ...f, ativo: e.target.checked }))} />} label="Ativo" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogUser(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveUser} disabled={!userForm.nome || !userForm.email || (!editUser && !userForm.senha)}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard;
